import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { Container } from '../../packages/core/di/Container';
import { i18n } from '../i18n';
import type { GetInventorySnapshotUseCase, InventoryItemDTO } from '../../packages/core/application/use-cases/GetInventorySnapshotUseCase';
import type { FindMovementsUseCase, MovementListItemDTO } from '../../packages/core/application/use-cases/FindMovementsUseCase';

export const useInventoryStore = defineStore('inventory', () => {
  // State
  const inventoryItems = ref<InventoryItemDTO[]>([]);
  const movements = ref<MovementListItemDTO[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const itemsOutOfStock = computed(() => inventoryItems.value.filter((i) => i.stockStatus === 'OUT_OF_STOCK'));
  const itemsLowStock = computed(() => inventoryItems.value.filter((i) => i.stockStatus === 'LOW_STOCK'));

  const filteredItems = computed(
    () =>
      (search: string, warehouseId?: string) => {
        let items = inventoryItems.value;
        if (search.trim()) {
          const q = search.trim().toLowerCase();
          items = items.filter(
            (i) =>
              i.productName.toLowerCase().includes(q) || i.productSku.toLowerCase().includes(q)
          );
        }
        return items;
      }
  );

  const filteredMovements = computed(
    () =>
      (search: string, type?: string) => {
        let result = movements.value;
        if (type && type !== 'ALL') {
          result = result.filter((m) => m.movementType === type);
        }
        if (search.trim()) {
          const q = search.trim().toLowerCase();
          result = result.filter(
            (m) =>
              m.productName.toLowerCase().includes(q) || m.productSku.toLowerCase().includes(q)
          );
        }
        return result;
      }
  );

  // Actions
  const container = Container.getInstance();

  const fetchInventorySnapshot = async (filters?: { warehouseId?: string }) => {
    loading.value = true;
    error.value = null;
    try {
      const useCase = container.get<GetInventorySnapshotUseCase>('GetInventorySnapshotUseCase');
      inventoryItems.value = await useCase.execute(filters);
    } catch (err) {
      error.value = err instanceof Error ? err.message : i18n.global.t('errors.fetchInventory');
      console.error('Error fetching inventory snapshot:', err);
    } finally {
      loading.value = false;
    }
  };

  const fetchMovements = async (filters?: {
    productId?: string;
    warehouseId?: string;
    type?: 'IN' | 'OUT' | 'TRANSFER' | 'ADJUSTMENT';
    startDate?: Date;
    endDate?: Date;
  }) => {
    loading.value = true;
    error.value = null;
    try {
      const useCase = container.get<FindMovementsUseCase>('FindMovementsUseCase');
      movements.value = await useCase.execute(filters);
    } catch (err) {
      error.value = err instanceof Error ? err.message : i18n.global.t('errors.fetchMovements');
      console.error('Error fetching movements:', err);
    } finally {
      loading.value = false;
    }
  };

  const clearError = () => {
    error.value = null;
  };

  return {
    // State
    inventoryItems,
    movements,
    loading,
    error,
    // Getters
    itemsOutOfStock,
    itemsLowStock,
    filteredItems,
    filteredMovements,
    // Actions
    fetchInventorySnapshot,
    fetchMovements,
    clearError,
  };
});
